import type { GetApiResponseByUrl, paths } from '@crossbeam/openapi';
import { ApiEndpoints } from '@crossbeam/openapi/src/api';

import { HttpResponse, passthrough } from 'msw';
import { SetupWorker } from 'msw/browser';
import { createOpenApiHttp } from 'openapi-msw';

import statusPageEvents from '@test/mocks/default/axios.statuspage-active.json';
import trayResponse from '@test/temp-handlers/tray-response';

import appConfig from '@/config';
import urls from '@/urls';

const typedHttp = createOpenApiHttp<paths>({
  baseUrl: appConfig.apiBaseUrl,
});

const isTestEnv = process.env.NODE_ENV === 'test';
const enableMocks =
  import.meta.env.DEV &&
  import.meta.env.VITE_USE_MOCKS === 'true' &&
  !isTestEnv;

const MOCK_SLACK_CHANNELS = [
  { id: 'C0MOCKGEN', name: 'general', channel_type: 'public-channel' },
  { id: 'C0MOCKPRIV', name: 'partner-team', channel_type: 'private-channel' },
  {
    id: 'C0MOCKSHARED',
    name: 'ext-partner-alerts',
    channel_type: 'shared-channel',
  },
];

let worker: SetupWorker;
const initializeMockServer = async () => {
  if (!enableMocks) return;

  const { setupWorker } = await import('msw/browser');
  worker = setupWorker();

  /* enable bypass mode to allow un-mocked requests through */
  await worker.start({ onUnhandledRequest: 'bypass' });

  worker.use(
    typedHttp.untyped.get(
      appConfig.statuspageBaseUrl + urls.external.activeStatusPageEvents,
      () => {
        return HttpResponse.json({
          ...statusPageEvents.page,
          scheduled_maintenances: [],
        });
      },
    ),
    typedHttp.untyped.get(appConfig.apiBaseUrl + urls.connections.all, () => {
      return HttpResponse.json({
        items: [
          {
            config: { app_version: '1.68', instance_url: 'https://foo.com' },
            integration_type: 'salesforce',
            connected_by_user_email: 'shmaryeh@foo.com',
            date_last_pushed: '2023-06-09T13:38:22.000+00:00',
            connected_at: '2023-06-09T13:38:22.000+00:00',
          },
          {
            id: '1234-1234-1234-1234',
            config: { app_version: 51, instance_url: 'https://foo1.com' },
            is_paused: false,
            integration_type: 'snowflake',
            connected_by_user_email: 'shmaryeh@foo.com',
            date_last_pushed: '2023-06-09T13:38:22.000+00:00',
            connected_at: '2023-06-09T13:38:22.000+00:00',
          },
          {
            id: '5678-5678-5678-5678',
            is_paused: false,
            integration_type: 'databricks',
            connected_by_user_email: 'shmaryeh@foo.com',
            date_last_pushed: null,
            connected_at: '2026-04-20T17:24:55.000+00:00',
          },
        ],
      });
    }),
    typedHttp.get('/v0.1/connections/databricks', () => {
      return HttpResponse.json<
        GetApiResponseByUrl<'/v0.1/connections/databricks', 'get'>
      >({
        push_enabled: true,
        catalog: 'crossbeam_catalog',
        schema: 'crossbeam_schema',
        date_last_pushed: null,
      });
    }),
    // typedHttp.untyped.get(appConfig.apiBaseUrl + urls.feeds.all, () => {
    //   return HttpResponse.json({
    //     items: [
    //       {
    //         id: 80187,
    //         integration: { type: 'snowflake', friendly_name: 'Snowflake' },
    //         status: 'CONN_CHECK_FAILED',
    //         error_message:
    //           "Crossbeam reads your shared tables from a schema named '_CROSSBEAM', but none were found there. We found your tables in schema(s): PUBLIC. Please make sure your tables (accounts, contacts, deals, leads, users) are shared from a schema named exactly '_CROSSBEAM' (note the leading underscore), then reconnect.",
    //         external_user_email: 'jane.doe@example.com',
    //         connected_by: 'jane.doe@example.com',
    //         is_paused: false,
    //         is_syncing: false,
    //         updated_at: '2026-06-02T08:00:00.000Z',
    //         max_record_count: 5000000,
    //       },
    //     ],
    //   });
    // }),
    typedHttp.untyped.get(
      `${appConfig.salesEdgeBaseUrl}/extensions/status/third-party-integration-statuses`,
      () => {
        return HttpResponse.json({
          integrations: [
            {
              name: 'Hubspot',
              install_user: {
                email: 'shmaryeh@yahoo.lycos.co.uk.biz.info',
              },
              install_date: '2024-02-02T19:52:16.967173+00:00',
              is_active: true,
            },
          ],
        });
      },
    ),
    typedHttp.untyped.get(
      `${appConfig.salesEdgeBaseUrl}/cb_orgs/*/crossbeam-integration`,
      ({ params }) => {
        const status =
          localStorage.getItem('mock_crossbeam_integration_status') ?? 'NEW';
        if (status === 'NULL') return HttpResponse.json({ integration: null });
        return HttpResponse.json({
          integration: {
            provider: 'CROSSBEAM',
            status,
            last_sync_at: null,
            partners_last_sync_at: null,
            partners_unclassified_populations: null,
            org_uuid: (params as { 0?: string })[0] ?? 'mock-org-uuid',
            can_change_org_id: false,
            populations: [
              { name: 'Prospects', id: '1', status: 'COLD', is_standard: true },
              {
                name: 'Customers',
                id: '2',
                status: 'CLOSED:WON',
                is_standard: true,
              },
              {
                name: 'Open Opportunities',
                id: '3',
                status: 'PROSPECT',
                is_standard: true,
              },
            ],
          },
        });
      },
    ),
    typedHttp.get('/v0.1/tray-integrations', () =>
      HttpResponse.json(trayResponse),
    ),
    // localStorage.mock_slack_app_status: 'enabled' | 'disabled' | 'error'; anything else passes through
    typedHttp.get('/v0.1/slack-app', () => {
      const status = localStorage.getItem('mock_slack_app_status');
      if (status === 'enabled') {
        return HttpResponse.json({
          is_enabled: true,
          channel_list: MOCK_SLACK_CHANNELS,
        });
      }
      if (status === 'error') {
        return HttpResponse.json(
          // @ts-expect-error we don't have typed errors yet
          { error: 'Slack API rate limited' },
          { status: 500 },
        );
      }
      if (status === 'disabled') {
        return HttpResponse.json({ is_enabled: false });
      }
      return passthrough();
    }),
    typedHttp.get('/v0.1/slack-app/channels/{id}', ({ params }) => {
      if (localStorage.getItem('mock_slack_app_status') !== 'enabled') {
        return passthrough();
      }
      const channel = MOCK_SLACK_CHANNELS.find(({ id }) => id === params.id);
      if (!channel) {
        return HttpResponse.json(
          // @ts-expect-error we don't have typed errors yet
          { error: 'Channel not found' },
          { status: 404 },
        );
      }
      return HttpResponse.json({
        ...channel,
        has_unknown_partners: channel.channel_type === 'shared-channel',
        known_partner_org_ids: [],
      });
    }),
    // typedHttp.get('/v0.1/users/me', () =>
    //   HttpResponse.json(
    //     {
    //       authorizations: [],
    //       invite_requests: [],
    //       is_user_linkable: false,
    //       pending_invitations: [],
    //       user: {
    //         id: 999,
    //         email: 'shmaryeh@yahoo.lycos.co.uk.biz.info',
    //         first_name: 'Shmaryeh',
    //         last_name: 'Kobrinsky',
    //         created_at: '2023-01-01T00:00:00.000+00:00',
    //         active: true,
    //         picture_url: null,
    //         pronouns: null,
    //         job_title: 'Developer',
    //         department: 'Engineering',
    //         company_position: null,
    //         auth_svc_id: 'auth0|999999999999999999999',
    //       },
    //     },
    //     { status: 401 },
    //   ),
    // ),
  );
};

const registerMockEndpoints = (endpoints: ApiEndpoints) => {
  const isTestEnv = process.env.NODE_ENV === 'test';
  const enableMocks =
    import.meta.env.DEV &&
    import.meta.env.VITE_USE_MOCKS === 'true' &&
    !isTestEnv;

  if (!enableMocks) return;
  worker.use(
    ...endpoints.map(({ url, response = {} }) =>
      typedHttp.get(url, () =>
        HttpResponse.json(response as GetApiResponseByUrl<typeof url>),
      ),
    ),
  );
};

export {
  initializeMockServer,
  worker,
  typedHttp,
  registerMockEndpoints,
  enableMocks,
};
